import {
  faBuilding,
  faCalendarDays,
  faClipboardList,
  faEye,
  faRotateRight,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ChevronLeft, ChevronRight } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "../components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import { SurveyOverview } from "./EventDetail/SurveyOverview";
import { fetchSurveys } from "../services/SurveyService";
import "./Surveys.css";

const SURVEY_PAGE_SIZE = 25;

function createSurveyContexts(survey) {
  const eventContexts = (survey.events || []).map((event) => ({
    key: `${survey.id}-event-${event.id}`,
    type: "event",
    context: {
      eventId: event.id,
      eventTitle: event.title || `Esemény #${event.id}`,
    },
    survey,
  }));
  const clubContexts = (survey.clubs || []).map((club) => ({
    key: `${survey.id}-club-${club.id}`,
    type: "club",
    context: {
      clubId: club.id,
      clubTitle: club.name || `Klub #${club.id}`,
    },
    survey,
  }));

  if (!eventContexts.length && !clubContexts.length) {
    return [{
      key: `${survey.id}-general`,
      type: "general",
      context: {},
      survey,
    }];
  }

  return [...clubContexts, ...eventContexts];
}

function contextTitle(item) {
  return item.context.clubTitle || item.context.eventTitle || "Nincs hozzárendelés";
}

export function Surveys() {
  const [surveys, setSurveys] = useState([]);
  const [selectedContext, setSelectedContext] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [listPage, setListPage] = useState(1);

  const loadSurveys = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await fetchSurveys();
      const contexts = result.flatMap(createSurveyContexts);

      setSurveys(result);
      setSelectedContext((current) =>
        current ? contexts.find((item) => item.key === current.key) || null : null
      );
    } catch (requestError) {
      console.error(requestError);
      setError("A kérdőívek betöltése nem sikerült.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSurveys();
  }, [loadSurveys]);

  const contexts = useMemo(
    () => surveys.flatMap(createSurveyContexts),
    [surveys]
  );
  const filteredContexts = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLocaleLowerCase("hu-HU");

    return contexts.filter((item) => {
      if (typeFilter !== "all" && item.type !== typeFilter) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      return [item.survey.title, contextTitle(item)]
        .join(" ")
        .toLocaleLowerCase("hu-HU")
        .includes(normalizedSearch);
    });
  }, [contexts, searchTerm, typeFilter]);
  const listPageCount = Math.max(1, Math.ceil(filteredContexts.length / SURVEY_PAGE_SIZE));
  const pagedContexts = useMemo(
    () => filteredContexts.slice(
      (listPage - 1) * SURVEY_PAGE_SIZE,
      listPage * SURVEY_PAGE_SIZE
    ),
    [filteredContexts, listPage]
  );
  const listRangeStart = filteredContexts.length
    ? (listPage - 1) * SURVEY_PAGE_SIZE + 1
    : 0;
  const listRangeEnd = Math.min(listPage * SURVEY_PAGE_SIZE, filteredContexts.length);

  useEffect(() => {
    setListPage(1);
  }, [searchTerm, typeFilter]);

  useEffect(() => {
    if (listPage > listPageCount) {
      setListPage(listPageCount);
    }
  }, [listPage, listPageCount]);

  return (
    <main className="surveys-page">
      <header className="surveys-page-header">
        <div>
          <div className="surveys-page-eyebrow">Adminisztráció</div>
          <h1>Kérdőívek</h1>
        </div>
        <button className="surveys-refresh-button" onClick={loadSurveys} disabled={isLoading}>
          <FontAwesomeIcon icon={faRotateRight} />
          {isLoading ? "Frissítés..." : "Frissítés"}
        </button>
      </header>

      <section className="surveys-browser" aria-label="Kérdőívek listája">
        <div className="surveys-browser-toolbar">
          <div className="surveys-filter" role="group" aria-label="Kapcsolat típusa">
            <button
              className={typeFilter === "all" ? "is-active" : ""}
              onClick={() => setTypeFilter("all")}
            >
              Összes
            </button>
            <button
              className={typeFilter === "club" ? "is-active" : ""}
              onClick={() => setTypeFilter("club")}
            >
              <FontAwesomeIcon icon={faBuilding} /> Klubok
            </button>
            <button
              className={typeFilter === "event" ? "is-active" : ""}
              onClick={() => setTypeFilter("event")}
            >
              <FontAwesomeIcon icon={faCalendarDays} /> Események
            </button>
          </div>
          <label className="surveys-list-search">
            <FontAwesomeIcon icon={faSearch} />
            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Keresés kérdőív vagy hozzárendelés alapján"
            />
          </label>
        </div>

        {error ? <div className="surveys-list-message is-error">{error}</div> : null}
        {isLoading && !surveys.length ? (
          <div className="surveys-list-message">Kérdőívek betöltése...</div>
        ) : (
          <div className="surveys-context-list">
            <div className="surveys-context-list-header">
              <span>Kérdőív</span>
              <span>Hozzárendelés</span>
              <span>Kérdések</span>
              <span />
            </div>
            {pagedContexts.map((item) => (
              <button
                className={`surveys-context-row ${selectedContext?.key === item.key ? "is-selected" : ""}`}
                key={item.key}
                onClick={() => setSelectedContext(item)}
              >
                <span className="surveys-context-title">
                  <span className="surveys-context-icon">
                    <FontAwesomeIcon icon={faClipboardList} />
                  </span>
                  <strong>{item.survey.title}</strong>
                </span>
                <span className="surveys-context-target">
                  <span className={`surveys-context-badge is-${item.type}`}>
                    {item.type === "club" ? "Klub" : item.type === "event" ? "Esemény" : "Általános"}
                  </span>
                  {contextTitle(item)}
                </span>
                <span>{item.survey.questions.length}</span>
                <span className="surveys-context-open">
                  <FontAwesomeIcon icon={faEye} />
                  Megnyitás
                </span>
              </button>
            ))}
            {!filteredContexts.length ? (
              <div className="surveys-list-message">Nincs a szűrésnek megfelelő kérdőív.</div>
            ) : null}
            {filteredContexts.length ? (
              <div className="surveys-list-footer">
                <span>{listRangeStart}-{listRangeEnd} / {filteredContexts.length} kérdőív</span>
                <div className="surveys-list-pagination">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setListPage((page) => Math.max(1, page - 1))}
                    disabled={listPage === 1}
                    title="Előző oldal"
                  >
                    <ChevronLeft aria-hidden="true" size={18} />
                    <span className="sr-only">Előző oldal</span>
                  </Button>
                  <span>{listPage} / {listPageCount}</span>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setListPage((page) => Math.min(listPageCount, page + 1))}
                    disabled={listPage === listPageCount}
                    title="Következő oldal"
                  >
                    <ChevronRight aria-hidden="true" size={18} />
                    <span className="sr-only">Következő oldal</span>
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </section>

      <Sheet
        open={Boolean(selectedContext)}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedContext(null);
          }
        }}
      >
        <SheetContent className="w-[calc(100vw-24px)] max-w-[1180px] overflow-hidden p-0">
          <SheetHeader className="sr-only">
            <SheetTitle>{selectedContext?.survey.title || "Kérdőív"}</SheetTitle>
            <SheetDescription>A kérdőív beküldései és részletes válaszai.</SheetDescription>
          </SheetHeader>
          <div className="surveys-detail-sheet-body">
            {selectedContext ? (
              <SurveyOverview
                key={selectedContext.key}
                survey={selectedContext.survey}
                context={selectedContext.context}
              />
            ) : null}
          </div>
        </SheetContent>
      </Sheet>
    </main>
  );
}
